/**
 * Fine funnel — solo dopo call book/skip; indietro consentito.
 */
import { Link, useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { OnboardingShell } from "@/components/onboarding/OnboardingShell";
import {
  customerStepPath,
  firstIncompleteCustomerStep,
  isCustomerStepReachable,
} from "@/components/onboarding/onboardingSteps";
import {
  downloadOnboardingContractPdf,
  fetchOnboardingByToken,
} from "@/lib/onboarding-public";

export default function OnboardingCompletedPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["onboarding-public", token],
    enabled: !!token,
    queryFn: () => fetchOnboardingByToken(token!),
    retry: false,
    refetchInterval: (q) => {
      const ob = q.state.data?.onboarding;
      if (!ob || ob.access_ready) return false;
      return 4000;
    },
  });

  const downloadPdfMutation = useMutation({
    mutationFn: () => downloadOnboardingContractPdf(token!),
    onSuccess: ({ url }) => {
      window.open(url, "_blank", "noopener,noreferrer");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Blocca skip Fine se call non fatta
  useEffect(() => {
    if (!token || !data) return;
    if (!isCustomerStepReachable("completed", data)) {
      const fallback = firstIncompleteCustomerStep(data);
      navigate(customerStepPath(token, fallback), { replace: true });
    }
  }, [token, data, navigate]);

  if (!token) return null;

  if (isLoading || error || !data) {
    return (
      <OnboardingShell
        token={token}
        step="completed"
        loading={isLoading}
        error={error instanceof Error ? error.message : error ? "Errore" : null}
        hideContinue
      >
        {null}
      </OnboardingShell>
    );
  }

  const ob = data.onboarding;
  const accessReady = !!ob.access_ready;
  const callDone = ob.call_status === "booked" || ob.call_status === "skipped";
  const canDownloadContract = !!ob.terms_accepted_at;

  let title = "Quasi fatto";
  let body =
    "Completa gli step precedenti in ordine. Puoi tornare indietro agli step già fatti.";

  if (accessReady && ob.unlock_mode === "existing_trial") {
    title = "Accesso illimitato";
    body =
      "Il pagamento è confermato. Continua con le stesse credenziali della prova — non ci sono più scadenze.";
  } else if (accessReady) {
    title = "Attivazione completata";
    body =
      "Ti abbiamo inviato l’email con il link di accesso e le istruzioni per le credenziali.";
  } else if (ob.tenant_is_trial && ob.payment_succeeded_at) {
    title = "Prova attiva";
    body =
      "Hai già il login. Quando Mindlead conferma il bonifico, la prova diventa accesso illimitato. Nel frattempo puoi usare la piattaforma e gestire la call.";
  } else if (ob.payment_succeeded_at) {
    title = "In attesa bonifico";
    body =
      "Il canone con carta è ok. L’email di accesso arriverà dopo la conferma del bonifico.";
  }

  return (
    <OnboardingShell
      token={token}
      step="completed"
      tenantName={ob.tenant_name}
      payload={data}
      hideContinue
      onBack={() => navigate(`/onboarding/${token}/call`)}
    >
      <div className="rounded-2xl border border-border/70 bg-card p-6 text-center space-y-4">
        <h2 className="text-xl font-semibold tracking-tight">{title}</h2>
        <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>

        <div className="space-y-2 pt-1">
          {(accessReady || ob.tenant_is_trial) && (
            <Button asChild className="w-full rounded-xl h-11">
              <Link to="/construction/login">Accedi alla piattaforma</Link>
            </Button>
          )}
          {canDownloadContract && (
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-xl h-11"
              disabled={downloadPdfMutation.isPending}
              onClick={() => downloadPdfMutation.mutate()}
            >
              {downloadPdfMutation.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <FileDown className="mr-1.5 h-3.5 w-3.5" />
              )}
              Scarica contratto firmato
            </Button>
          )}
          {!callDone && (
            <Button asChild variant="outline" className="w-full rounded-xl h-11">
              <Link to={`/onboarding/${token}/call`}>Vai alla call</Link>
            </Button>
          )}
          {!accessReady && (
            <Button asChild variant="ghost" className="w-full rounded-xl">
              <Link to={`/onboarding/${token}/pagamento`}>Torna al pagamento</Link>
            </Button>
          )}
        </div>
      </div>
    </OnboardingShell>
  );
}
